import { API_CONFIG } from '../config/apiConfig';
import { LOOKUP_GROUPS } from '../config/apiConfig';

// ===== INTERFACES (Combined from both files) =====

export interface LookupValue {
  value: string;
  label: string;
  parentValue?: string;
}

export interface LookupLabel {
  label: string;
}

interface LookupApiResponse {
  total: number;
  size: number;
  data: Array<{
    lookup_label: string;
    lookup_value: string;
    parent_lookup_value?: string;
  }>;
}

interface LookupLabelApiResponse {
  total: number;
  size: number;
  data: Array<{
    lookup_label: string;
  }>;
}

// Interface for the /list_country endpoint response
interface CountryApiResponse {
  total: number;
  size: number;
  data: Array<{
    country_name: string;
    country_code: string;
    currency_code?: string;
    currency?: string;
  }>;
}

interface StateApiResponse {
  total: number;
  size: number;
  data: Array<{
    state_name: string;
    state_code: string;
    country_code?: string;
  }>;
}

interface CityApiResponse {
  total: number;
  size: number;
  data: Array<{
    city_name: string;
    city_code: string;
    state_code?: string;
    state?: string;
    country_code?: string;
  }>;
}

const getAuthHeader = (): string => {
  // Using a hardcoded token for demonstration, replace with dynamic token retrieval
  const token = "dXNlcjpQYXNzQDEyMzQ="; 
  if (!token) throw new Error('User not logged in. No auth token found.');
  return `Basic ${token}`;
};




export const fetchLookupValuesByGroup = async (lookupGroup: string): Promise<LookupValue[]> => {
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/list/lookup_value_by_group?lookupGroup=${lookupGroup}&pageSize=1000`,
      {
        headers: {
          'accept': 'application/json',
          'api_id': API_CONFIG.API_ID,
          'api_key': API_CONFIG.API_KEY,
          // 'Authorization': getAuthHeader(),
        }
      }
    );
    if (!response.ok) throw new Error(`HTTP error fetching ${lookupGroup}! status: ${response.status}`);
    
    const data = await response.json();
    
    if (data && Array.isArray(data.data)) {
      return data.data.map((item: any) => ({
        value: item.lookup_value,
        label: item.lookup_label,
        parentValue: item.parent_lookup_value
      }));
    }
    return [];
  } catch (error) {
    console.error(`Error fetching lookup values for group ${lookupGroup}:`, error);
    return []; // Return empty array on error to prevent crashes
  }
};

// ===== NEW AND MODIFIED FUNCTIONS =====

/**
 * [MODIFIED] Fetches both countries and currencies from a single API endpoint.
 * This function processes the country list to extract unique currencies.
 *
 * @returns A promise that resolves to an object containing arrays of countries and currencies.
 */
export const fetchCountriesAndCurrencies = async (): Promise<{ countries: LookupValue[], currencies: LookupValue[] }> => {
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/list/list_country?pageSize=300`,
      {
        headers: {
          'accept': 'application/json',
          'api_id': API_CONFIG.API_ID,
          'api_key': API_CONFIG.API_KEY,
          // 'Authorization': getAuthHeader(),
        }
      }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
    const data = await response.json();
    
    const countries: LookupValue[] = data?.data?.map((item: any) => ({ 
      label: item.country_name, 
      value: item.country_code 
    })) || [];

    const currencyMap = new Map<string, string>();
    data?.data?.forEach((item: any) => {
      if (item.currency_code && item.currency) {
        currencyMap.set(item.currency_code, item.currency);
      }
    });

    const currencies: LookupValue[] = Array.from(currencyMap, ([value, label]) => ({ label, value }));
    
    return { countries, currencies };

  } catch (error) {
    console.error('Error fetching countries and currencies:', error);
    return { countries: [], currencies: [] };
  }
};



export const fetchStates = async (countryCode: string): Promise<LookupValue[]> => {
  if (!countryCode) return [];
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/list/state_by_country?country_code=${countryCode}&pageSize=500`,
      {
        headers: {
          'accept': 'application/json',
          'api_id': API_CONFIG.API_ID,
          'api_key': API_CONFIG.API_KEY,
          // 'Authorization': getAuthHeader(),
        }
      }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data?.data?.map((item: any) => ({ label: item.state_name, value: item.state_code })) || [];
  } catch (error) {
    console.error(`Error fetching states for ${countryCode}:`, error);
    return [];
  }
};

export const fetchCities = async (countryCode: string, stateCode: string): Promise<LookupValue[]> => {
  if (!countryCode || !stateCode) return [];
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/list/city_by_state?country_code=${countryCode}&stateCode=${stateCode}&pageSize=10000`,
      {
        headers: {
          'accept': 'application/json',
          'api_id': API_CONFIG.API_ID,
          'api_key': API_CONFIG.API_KEY,
          // 'Authorization': getAuthHeader(),
        }
      }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data?.data?.map((item: any) => ({ value: item.city_code, label: item.city_name })) || [];
  } catch (error) {
    console.error(`Error fetching cities for ${countryCode}-${stateCode}:`, error);
    return [];
  }
};

// ===== CORE FUNCTIONS (Unchanged) =====

const fetchLookupValues = async (lookupGroup: string): Promise<LookupValue[]> => {
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/list/lookup_value_by_group?lookupGroup=${lookupGroup}&startOffset=0&pageSize=10000`,
      {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'api_id': API_CONFIG.API_ID,
          'api_key': API_CONFIG.API_KEY,
          // 'Authorization': getAuthHeader(),
        }
      }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data: LookupApiResponse = await response.json();
    if (data && Array.isArray(data.data)) {
      return data.data.map(item => ({
        value: item.lookup_value,
        label: item.lookup_label,
        parentValue: item.parent_lookup_value
      }));
    }
    return [];
  } catch (error) {
    console.error(`Error fetching lookup values for group ${lookupGroup}:`, error);
    return [];
  }
};

export const fetchLookupLabel = async (lookupGroup: string, lookupValue: string): Promise<LookupLabel[]> => {
  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/list/lookup_label_by_value_group?groupName=${lookupGroup}&lookupValue=${lookupValue}`,
      {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'api_id': API_CONFIG.API_ID,
          'api_key': API_CONFIG.API_KEY,
          // 'Authorization': getAuthHeader(),
        }
      }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data: LookupLabelApiResponse = await response.json();
    if (data && Array.isArray(data.data)) {
      return data.data.map(item => ({
        label: item.lookup_label,
      }));
    }
    return [];
  } catch (error) {
    console.error(`Error fetching lookup label for ${lookupGroup}-${lookupValue}:`, error);
    return [];
  }
};

const lookupCache: Record<string, LookupValue[]> = {};

export const getLookupValues = async (lookupGroup: string): Promise<LookupValue[]> => {
  if (lookupCache[lookupGroup]) {
    return lookupCache[lookupGroup];
  }
  const values = await fetchLookupValues(lookupGroup);
  lookupCache[lookupGroup] = values;
  return values;
};

export const getChildSubTypes = async (parentValue: string, childSubTypeGroup: string): Promise<LookupValue[]> => {
  const allSubTypes = await getLookupValues(childSubTypeGroup);
  return allSubTypes.filter(subType => subType.parentValue === parentValue);
};